import { useEffect, useRef } from 'react';

/**
 * Adds the `visible` class to any `.fade-in` descendant once it scrolls into
 * view, then unobserves it. Mirrors the fadeObserver every page's inline
 * script used to register.
 *
 * Mounted ONCE at the app root so there is exactly one observer per page —
 * the original site was at risk of double-registering these because main.js
 * and the per-page inline IIFE both had copies of the logic.
 */
export function useScrollReveal(): void {
  useEffect(() => {
    const nodes = document.querySelectorAll<HTMLElement>('.fade-in');

    // Respect reduced-motion: reveal everything immediately, skip animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((n) => {
        n.classList.add('visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    nodes.forEach((n) => {
      observer.observe(n);
    });
    return () => observer.disconnect();
  }, []);
}

/**
 * Observes a single element and reports the first time it becomes visible.
 * Used by the counters and the carousel autoplay gate.
 */
export function useInView<T extends HTMLElement>(
  onEnter: () => void,
  threshold = 0.4,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  // Keep the latest callback without re-running the effect.
  const cb = useRef(onEnter);
  cb.current = onEnter;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cb.current();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return ref;
}
